// src/components/EventPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurifyLib from "dompurify";
import { useDispatch, useSelector } from "react-redux";
import SEO from "./SEO.jsx";
import {
  fetchEvents,
  selectAllEvents,
  selectEventsError,
  selectEventsStatus,
} from "../content/eventsSlice";

// ──────────────────────────────────────
// Config & helpers
// ──────────────────────────────────────
const BASE_URL = (import.meta?.env?.VITE_SITE_URL) || "https://werlocal.us";

const slugify = (s = "") =>
  String(s)
    .toLowerCase()
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const eq = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();
const looksLikeCounty = (s) => /county$/i.test(String(s || ""));

/** Prefer a true city: explicit city, or label if it isn't state/county */
const pickCity = ({ city, county, state, label }) => {
  if (city) return city;
  const lbl = (label || "").trim();
  if (!lbl) return "";
  if (eq(lbl, state) || eq(lbl, county) || looksLikeCounty(lbl)) return "";
  return lbl; // e.g., "Dana Point"
};

const DOMPurify = typeof window !== "undefined" && DOMPurifyLib
  ? DOMPurifyLib
  : { sanitize: (x) => x };

if (DOMPurify.addHook) {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width:${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpoint]);
  return isMobile;
}

const decode = (s) => String(s ?? "");
const sanitize = (html) =>
  DOMPurify.sanitize(decode(html || ""), {
    KEEP_CONTENT: true,
    ALLOWED_TAGS: [
      "p","a","strong","em","u","s","br","hr",
      "ul","ol","li","blockquote","pre","code",
      "h1","h2","h3","h4","h5","h6","img","figure","figcaption"
    ],
    ALLOWED_ATTR: [
      "href","title","target","rel",
      "src","srcset","sizes","alt","width","height"
    ],
    FORBID_TAGS: ["script","iframe"],
    FORBID_ATTR: ["onerror","onclick","onload","style","class"]
  });

function parseDT(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  let s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:[+-]\d{2}:\d{2}|Z)?$/.test(s)) s = s.replace(" ", "T");
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) s = s.replace(" ", "T");
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
const fmtDT = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit"
  });
};
const renderWhen = (from, to) => {
  const f = fmtDT(from), t = fmtDT(to);
  if (!f && !t) return "TBA";
  if (f && !t) return `From: ${f}`;
  if (!f && t) return `To: ${t}`;
  return `From: ${f} — To: ${t}`;
};
const stripTags = (html = "") => {
  const el = document.createElement("div"); el.innerHTML = html;
  return el.textContent || el.innerText || "";
};

// ───────── ReadMore
function ReadMore({ html, lines = 6, className = "" }) {
  const [open, setOpen] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const boxRef = useRef(null);
  const clean = useMemo(() => sanitize(html || ""), [html]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    el.setAttribute("data-open", "false");
    requestAnimationFrame(() => {
      const needs = el.scrollHeight > el.clientHeight + 1;
      setShowToggle(needs);
      el.setAttribute("data-open", open ? "true" : "false");
    });
  }, [clean, open, lines]);

  const btnStyle = {
    display: "inline-flex",
    alignItems: "center",
    background: "#4f46e5",
    color: "#fff",
    border: 0,
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 700,
    marginTop: 10,
    cursor: "pointer",
  };

  return (
    <div>
      <article
        ref={boxRef}
        className={`ck-prose ${className}`}
        data-open={open ? "true" : "false"}
        style={
          open
            ? undefined
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: String(lines),
                overflow: "hidden",
              }
        }
        dangerouslySetInnerHTML={{ __html: clean }}
      />
      {showToggle && (
        <button type="button" onClick={() => setOpen((v) => !v)} style={btnStyle}>
          {open ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
}

// ───────── Row
function EventRow({ ev }) {
  const isMobile = useIsMobile(640);
  const placeholder =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'>
         <rect width='100%' height='100%' fill='#e5e7eb'/>
         <text x='50%' y='52%' dominant-baseline='middle' text-anchor='middle'
               font-family='system-ui,sans-serif' font-size='12' fill='#64748b'>no image</text>
       </svg>`
    );
  const img = ev?.image_small || ev?.thumbnail || ev?.thumb || ev?.image || placeholder;

  const topic = ev?.topic || "Event";
  const business = ev?.business || "";
  const city = ev?.city || "";
  const state = ev?.state || "";
  const place = [city, state].filter(Boolean).join(", ");

  const cardStyle = {
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    width: "100%",
  };
  const row = {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "stretch" : "flex-start",
    gap: 14,
  };
  const thumbWrap = { flex: isMobile ? "0 0 auto" : "0 0 120px", width: isMobile ? "100%" : 120 };
  const thumb = {
    width: isMobile ? "100%" : 120,
    height: isMobile ? "auto" : 120,
    aspectRatio: isMobile ? "16 / 9" : "auto",
    borderRadius: 10,
    objectFit: "cover",
    display: "block",
    border: "1px solid #e2e8f0",
  };
  const body = { flex: 1, minWidth: 0 };
  const h3 = {
    fontSize: 20, lineHeight: 1.2, fontWeight: 800, color: "#0f172a",
    margin: isMobile ? "10px 0 0" : 0, wordBreak: "break-word", overflowWrap: "anywhere"
  };
  const meta = { marginTop: 4, color: "#475569", fontSize: 13, wordBreak: "break-word" };
  const metaLine = { marginTop: 6, color: "#475569", fontSize: 13 };
  const dot = { margin: "0 6px", color: "#94a3b8" };

  return (
    <article style={cardStyle}>
      <div style={row}>
        <div style={thumbWrap}>
          <img src={img} alt={business || topic} loading="lazy" style={thumb} />
        </div>
        <div style={body}>
          <h3 style={h3}>{topic}</h3>
          <div style={meta}>
            {business && <span style={{ fontWeight: 600 }}>{business}</span>}
            {place && (<><span style={dot}>•</span><span>{place}</span></>)}
          </div>
          <div style={metaLine}>{renderWhen(ev.ffrom, ev.tto)}</div>
          {ev.description && <ReadMore html={ev.description} lines={3} className="mt-2" />}
          {(ev?.link || ev?.video) && (
            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {ev?.link && (
                <a
                  href={ev.link}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: 12, fontWeight: 600, color: "#0f172a", textDecoration: "none",
                    border: "1px solid #e2e8f0", padding: "6px 10px", borderRadius: 8, background: "#fff",
                  }}
                >
                  Details
                </a>
              )}
              {ev?.video && (
                <a
                  href={ev.video}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: 12, fontWeight: 600, color: "#0f172a", textDecoration: "none",
                    border: "1px solid #e2e8f0", padding: "6px 10px", borderRadius: 8, background: "#fff",
                  }}
                >
                  Video
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

// ───────── Page
export default function EventPage() {
  const dispatch = useDispatch();

  // Raw location from Redux
  const loc = useSelector((s) => s.local?.location) || {};
  const { city, county, state, label } = loc;

  // 1) Pick effective city for both data & SEO

  const effectiveCity = pickCity(loc);
  const effectiveCounty =
  looksLikeCounty(label) ? label :
  (looksLikeCounty(county) ? county : "");

  // Header display chip
  const display = effectiveCity || city || label || county || state || "California";
  console.log("display", display)

  // Data status
  const status = useSelector(selectEventsStatus);
  const error = useSelector(selectEventsError);
  const all = useSelector(selectAllEvents);

  // 2) Fetch: prefer city, else state
  useEffect(() => {
  const params =
    effectiveCity
      ? { city: effectiveCity }
      : effectiveCounty
        ? { city: effectiveCounty }
        : { city: state || "California" };

  console.log("params", params);
  dispatch(fetchEvents(params));
}, [dispatch, effectiveCity, effectiveCounty, state]);

  // Search/filter
  const [q, setQ] = useState("");
  const events = useMemo(() => {
    let arr = Array.isArray(all) ? all.slice() : [];
    const s = q.trim().toLowerCase();
    if (s) {
      arr = arr.filter((e) =>
        [e?.topic, e?.business, e?.city, e?.state, stripTags(e?.description || "")]
          .filter(Boolean).join(" ").toLowerCase().includes(s)
      );
    }
    arr.sort(
      (a, b) =>
        (parseDT(b?.ffrom ?? b?.from)?.getTime() || 0) -
        (parseDT(a?.ffrom ?? a?.from)?.getTime() || 0)
    );
    return arr;
  }, [all, q]);

  const sortEventsByStart = (arr = []) =>
    [...arr].sort((a, b) => {
      const ta = Date.parse(a?.ffrom || a?.from || a?.tto || a?.to || 0) || 0;
      const tb = Date.parse(b?.ffrom || b?.from || b?.tto || b?.to || 0) || 0;
      return ta - tb;
    });
  const sorted = useMemo(() => sortEventsByStart(events), [events]);

  // Layout styles
  const wrap = { maxWidth: 960, margin: "0 auto", padding: "0 16px 32px" };
  const h1Style = { fontSize: 34, fontWeight: 800, color: "#0f172a", margin: "22px 0 6px"  };
  
  
  const sub = { color: "#475569", margin: "0 0 14px" };
  const input = { width: "100%", maxWidth: 340, border: "1px solid #d1d5db", borderRadius: 12, padding: "10px 12px", outline: "none", fontSize: 14 };
  const listGap = { display: "grid", gap: 12 };

  // ======= SEO (consistent across H1, <title>, meta) =======
  const locationText = effectiveCity
  ? `${effectiveCity}${state ? `, ${state}` : ""}`
  : effectiveCounty
    ? `${effectiveCounty}${state ? `, ${state}` : ""}`
    : (state || "California");  

  const seoTitle = `Events in ${locationText}`;
  const description = effectiveCity
    ? `Find local events and things to do in ${effectiveCity}${state ? `, ${state}` : ""}. Updated daily.`
    : `Discover upcoming events across ${state || "California"}.`;
  const encodeSegment = (s = "") => encodeURIComponent(String(s).trim());
  const path = effectiveCity
    ? `/local/${encodeSegment(effectiveCity)}`
    : effectiveCounty
    ? `/local/${encodeSegment(effectiveCounty)}`
    : "";

  const url = `${BASE_URL}${path}`;
const ogImage = `${BASE_URL}/og/${state ? slugify(state) : "california"}${
  effectiveCity
    ? `-${slugify(effectiveCity)}`
    : effectiveCounty
      ? `-${slugify(effectiveCounty)}`
      : ""
}.png`;
  const robots = (events?.length ?? 0) > 0 ? "index,follow" : "noindex,follow";

  // CollectionPage JSON-LD
  const collectionJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: seoTitle,
    url,
    description,
    isPartOf: { "@type": "WebSite", name: "We Are Local", url: BASE_URL },
  };

  // Event JSON-LD (limit to 25)
  const toISO = (x) => {
    const d = parseDT(x);
    return d ? d.toISOString() : undefined;
  };
  const eventJsonLd = sorted.slice(0, 25).map((e) => {
    const name = e?.topic || e?.title || "Event";
    const img = e?.image || e?.image_small || e?.thumbnail || undefined;
    const cityTxt = e?.city || undefined;
    const stateTxt = e?.state || undefined;
    const placeTxt = [cityTxt, stateTxt].filter(Boolean).join(", ");
    const slug = slugify(`${name}-${toISO(e?.ffrom) || ""}`) || (e?.id ? String(e.id) : "");
    const eventUrl = `${url}#event-${slug}`;

    return {
      "@context": "https://schema.org",
      "@type": "Event",
      name,
      startDate: toISO(e?.ffrom || e?.from),
      endDate: toISO(e?.tto || e?.to),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      url: e?.link || eventUrl,
      image: img ? [img] : undefined,
      description: stripTags(e?.description || ""),
      location: {
        "@type": "Place",
        name: e?.business || placeTxt || "Venue",
        address: placeTxt || undefined,
      },
      organizer: e?.business ? { "@type": "Organization", name: e.business } : undefined,
    };
  });

  return (
    <>
      <SEO
        title={seoTitle}
        description={description}
        canonical={url}
        robots={robots}
        og={{ url, title: seoTitle, description, type: "website", image: ogImage }}
        twitter={{ card: "summary_large_image", title: seoTitle, description, image: ogImage, site: "@werlocal" }}
        jsonLd={[collectionJsonLd, ...eventJsonLd]}
      />

      <div style={wrap}>
        <header>
          <h2 className="mb-3 text-capitalize">{seoTitle}</h2>
          <p style={sub}>Local happenings, neatly organized.</p>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search events, topics, city…"
            style={input}
          />
        </header>

        {status === "failed" && (
          <div style={{ marginTop: 12, border: "1px solid #fecaca", background: "#fff1f2", color: "#7f1d1d", padding: 12, borderRadius: 12 }}>
            Failed to load events: {error || "Unknown error"}
          </div>
        )}

        {status === "loading" && events.length === 0 && (
          <div style={{ marginTop: 12, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", padding: 16, borderRadius: 12, textAlign: "center" }}>
            Loading events…
          </div>
        )}

        {status !== "loading" && events.length === 0 && (
          <div style={{ marginTop: 12, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", padding: 16, borderRadius: 12, textAlign: "center" }}>
            No events match your filters.
          </div>
        )}

        <section style={{ marginTop: 14, ...listGap }}>
          {sorted.map((ev) => (
            <EventRow
              key={ev.id || `${ev.business}-${ev.ffrom || ev.from || ""}`}
              ev={ev}
            />
          ))}
        </section>
      </div>
    </>
  );
}